/**
 * blog_editor_controller.js — Tiptap rich-text editor for the admin blog composer
 * (chippie-2w2h9.3, part of the DB-backed admin blog engine epic).
 *
 * Wraps Tiptap's vanilla core (no React) as an element-scoped Stimulus controller.
 * connect() builds an Editor over `editorTarget`; every `onUpdate` mirrors
 * `editor.getJSON()` / `editor.getHTML()` into two hidden inputs (`bodyJsonTarget`,
 * `bodyHtmlTarget`) so a normal HTML form POST carries both representations with no
 * extra JS on the submitter's side. Toolbar buttons in the (separately-authored)
 * admin template call the action methods below, e.g.
 * `data-action="click->blog-editor#toggleBold"`.
 *
 * Initial content precedence: `bodyJsonTarget`'s value (a stringified ProseMirror
 * doc) wins when present and parses cleanly; otherwise falls back to
 * `bodyHtmlTarget`'s value (an HTML string — Tiptap's `content` option parses HTML
 * directly), so drafts imported before body_json existed stay editable.
 *
 * Custom node: `callout` (group: "block", content: "block+") round-trips as
 * `<div class="callout callout-<type>" data-callout-type="<type>">...</div>`;
 * `<type>` is one of info/warning/tip (free-form string, default "info").
 *
 * Every mark and node registered here must also be permitted by
 * `sanitize_blog_html` (application/utils/html_sanitizer.py), which is the
 * single owner of what survives to storage — anything the editor can emit but
 * the allowlist omits is stripped on save with no error, so the two lists are
 * kept in step and `tests/test_html_sanitizer.py` asserts it control by control.
 *
 * Tiptap is loaded on demand (esm/_tiptap.js), so the editor exists a moment
 * after connect() and the two custom extensions below are built from the loaded
 * module rather than at import time.
 */
import { Controller } from "@hotwired/stimulus";
import { loadTiptap } from "./_tiptap.js";

const createCallout = ({ Node, mergeAttributes }) => Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      type: {
        default: "info",
        parseHTML: (element) => element.getAttribute("data-callout-type") || "info",
        renderHTML: (attributes) => ({ "data-callout-type": attributes.type }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.callout" }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const type = node.attrs.type || "info";
    return [
      "div",
      mergeAttributes(HTMLAttributes, { class: `callout callout-${type}` }),
      0,
    ];
  },

  addCommands() {
    return {
      setCallout:
        (attributes) =>
        ({ commands }) =>
          commands.wrapIn(this.name, attributes),
      unsetCallout:
        () =>
        ({ commands }) =>
          commands.lift(this.name),
    };
  },
});

// Text alignment, re-rendered as a class instead of Tiptap's default inline
// `style="text-align: …"`. The blog sanitiser deliberately does not allow a
// `style` attribute (nh3 does not parse CSS, so allowing it would wave through
// `background:url(...)` trackers and `position:fixed` overlays on every
// paragraph); it does already allow a fixed set of classes per tag, which is
// how the Callout node round-trips. Aligning on that mechanism keeps the
// sanitiser's attack surface unchanged. `parseHTML` still reads the inline
// style so content pasted in from elsewhere keeps its alignment.
const ALIGNMENTS = ["left", "center", "right", "justify"];

const createTextAlignClass = ({ TextAlign }) => TextAlign.extend({
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: this.options.defaultAlignment,
            parseHTML: (element) => {
              const fromClass = ALIGNMENTS.find((a) =>
                element.classList.contains(`text-align-${a}`),
              );
              const alignment = fromClass || element.style.textAlign;
              return this.options.alignments.includes(alignment)
                ? alignment
                : this.options.defaultAlignment;
            },
            renderHTML: (attributes) => {
              if (!attributes.textAlign) return {};
              return { class: `text-align-${attributes.textAlign}` };
            },
          },
        },
      },
    ];
  },
});

export default class extends Controller {
  static targets = [
    "editor",
    "bodyJson",
    "bodyHtml",
    "linkUrl",
    "imageUrl",
    "youtubeUrl",
    "stateButton",
    "headingIcon",
  ];
  static values = { uploadUrl: String };

  connect() {
    this._mount();
  }

  async _mount() {
    let tiptap;
    try {
      tiptap = await loadTiptap();
    } catch (_e) {
      if (typeof window.showNotification === "function") {
        window.showNotification("The editor could not load. Check your connection and reload.", "error");
      }
      return;
    }
    // Disconnected, or already mounted by a later connect, while loading.
    if (!this.element.isConnected || this.editor) return;
    this._buildEditor(tiptap);
  }

  _buildEditor(tiptap) {
    const { Editor, StarterKit, TaskItem, Table } = tiptap;
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        // Link and Underline ship inside StarterKit v3 — configuring them here
        // rather than adding the standalone extensions avoids registering a
        // duplicate extension name, which Tiptap warns about and which leaves
        // whichever copy loses the race holding the default options.
        StarterKit.configure({ link: { openOnClick: false } }),
        tiptap.Highlight,
        tiptap.Superscript,
        tiptap.Subscript,
        createTextAlignClass(tiptap).configure({
          types: ["heading", "paragraph"],
          alignments: ALIGNMENTS,
        }),
        tiptap.TaskList,
        TaskItem.configure({ nested: true }),
        tiptap.Image,
        Table.configure({ resizable: true }),
        tiptap.TableRow,
        tiptap.TableHeader,
        tiptap.TableCell,
        tiptap.Youtube,
        createCallout(tiptap),
      ],
      content: this._initialContent(),
      onUpdate: ({ editor }) => this._syncHiddenFields(editor),
      onTransaction: () => this._refreshToolbarState(),
    });

    // Sync once immediately so the hidden fields reflect the parsed initial
    // content (covers the body_html fallback case, where bodyJson starts
    // empty until the first edit).
    this._syncHiddenFields(this.editor);
    this._refreshToolbarState();
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }

  _initialContent() {
    const raw = this.hasBodyJsonTarget ? this.bodyJsonTarget.value.trim() : "";
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch (_e) {
        // Malformed JSON — fall through to the HTML source below.
      }
    }
    return this.hasBodyHtmlTarget ? this.bodyHtmlTarget.value : "";
  }

  _syncHiddenFields(editor) {
    if (this.hasBodyJsonTarget) {
      this.bodyJsonTarget.value = JSON.stringify(editor.getJSON());
    }
    if (this.hasBodyHtmlTarget) {
      this.bodyHtmlTarget.value = editor.getHTML();
    }
  }

  // Reads a URL either from the triggering button's data-*-url-param (Stimulus
  // action params, e.g. data-blog-editor-url-param="https://...") or from a
  // named sibling target — deliberately NOT window.prompt(), which breaks
  // under Turbo navigation and Hotwire Native web views.
  _paramOrTargetValue(event, paramName, targetBaseName) {
    if (event && event.params && event.params[paramName]) {
      return event.params[paramName];
    }
    const hasProp = `has${targetBaseName[0].toUpperCase()}${targetBaseName.slice(1)}Target`;
    if (this[hasProp]) {
      return this[`${targetBaseName}Target`].value;
    }
    return null;
  }

  // Reflects the cursor's current formatting onto the toolbar, so a button
  // reads as pressed when the caret sits inside what it produces. Each button
  // opting in carries `data-blog-editor-target="stateButton"` plus the name of
  // the node/mark to query (`data-active-name`) and, where one name covers
  // several buttons, the attributes that distinguish them (`data-active-attrs`,
  // e.g. `{"level":2}` for H2). Driven off `onTransaction` rather than
  // `onUpdate` because moving the caret changes what is active without
  // changing the document.
  _refreshToolbarState() {
    if (!this.hasStateButtonTarget) return;
    for (const button of this.stateButtonTargets) {
      const name = button.dataset.activeName;
      let attrs;
      try {
        attrs = button.dataset.activeAttrs
          ? JSON.parse(button.dataset.activeAttrs)
          : undefined;
      } catch (_e) {
        attrs = undefined;
      }
      if (!name && !attrs) continue;
      // Tiptap has two isActive shapes: (name, attrs) for a specific node or
      // mark, and (attrs) alone for an attribute that spans several node types.
      // Alignment needs the second — it lives on both paragraphs and headings —
      // so a button may supply attrs with no name.
      const isActive = name
        ? this.editor.isActive(name, attrs)
        : this.editor.isActive(attrs);
      button.classList.toggle("active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    }
    this._refreshHeadingIcon();
  }

  // The heading dropdown's own glyph reports the level the caret is currently
  // in, so a collapsed menu still says what the block is. The level is read
  // from the active node rather than matched against a hard-coded list, so
  // adding or removing a level in the template needs no change here.
  _refreshHeadingIcon() {
    if (!this.hasHeadingIconTarget) return;
    const level = this.editor.isActive("heading")
      ? this.editor.getAttributes("heading").level
      : null;
    for (const icon of this.headingIconTargets) {
      icon.className = level ? `bi bi-type-h${level}` : "bi bi-text-paragraph";
    }
  }

  // --- Toolbar actions (data-action="blog-editor#toggleBold" etc.) ---

  undo() {
    this.editor.chain().focus().undo().run();
  }

  redo() {
    this.editor.chain().focus().redo().run();
  }

  // Level arrives as data-blog-editor-level-param="2" on the dropdown item.
  setHeading(event) {
    const level = Number(event?.params?.level);
    if (!level) return;
    this.editor.chain().focus().toggleHeading({ level }).run();
  }

  setParagraph() {
    this.editor.chain().focus().setParagraph().run();
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run();
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run();
  }

  toggleStrike() {
    this.editor.chain().focus().toggleStrike().run();
  }

  toggleUnderline() {
    this.editor.chain().focus().toggleUnderline().run();
  }

  toggleCode() {
    this.editor.chain().focus().toggleCode().run();
  }

  toggleHighlight() {
    this.editor.chain().focus().toggleHighlight().run();
  }

  toggleSuperscript() {
    this.editor.chain().focus().toggleSuperscript().run();
  }

  toggleSubscript() {
    this.editor.chain().focus().toggleSubscript().run();
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run();
  }

  toggleTaskList() {
    this.editor.chain().focus().toggleTaskList().run();
  }

  toggleBlockquote() {
    this.editor.chain().focus().toggleBlockquote().run();
  }

  toggleCodeBlock() {
    this.editor.chain().focus().toggleCodeBlock().run();
  }

  setHorizontalRule() {
    this.editor.chain().focus().setHorizontalRule().run();
  }

  // Alignment arrives as data-blog-editor-align-param="center".
  setTextAlign(event) {
    const align = event?.params?.align;
    if (!ALIGNMENTS.includes(align)) return;
    this.editor.chain().focus().toggleTextAlign(align).run();
  }

  setLink(event) {
    const url = this._paramOrTargetValue(event, "url", "linkUrl");
    if (!url) return;
    this.editor.chain().focus().setLink({ href: url }).run();
  }

  insertTable() {
    this.editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  }

  // Accepts an image URL (from an action param, the imageUrlTarget, or the
  // uploadImage handler below) and hands it to Tiptap.
  setImage(event) {
    const url = this._paramOrTargetValue(event, "url", "imageUrl");
    if (!url) return;
    this.editor.chain().focus().setImage({ src: url }).run();
  }

  // Fired by a hidden file input's change event (data-action="change->blog-editor#uploadImage").
  // Uploads to the admin endpoint and inserts the returned URL as an image.
  async uploadImage(event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = ""; // allow re-picking the same file later
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(this.uploadUrlValue, { method: "POST", body: formData });
      if (!res.ok) return;
      const data = await res.json();
      if (data.url) this.editor.chain().focus().setImage({ src: data.url }).run();
    } catch (_e) {
      // Silent -- the author can still paste an image URL manually.
    }
  }

  setYoutubeVideo(event) {
    const url = this._paramOrTargetValue(event, "url", "youtubeUrl");
    if (!url) return;
    this.editor.chain().focus().setYoutubeVideo({ src: url }).run();
  }

  // Alias so the toolbar can wire either verb.
  toggleYoutube(event) {
    this.setYoutubeVideo(event);
  }

  setCallout(event) {
    const type = (event.params && event.params.type) || "info";
    this.editor.chain().focus().setCallout({ type }).run();
  }
}
